import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { getActiveTeamId } from '../activeTeam';
import {
  useTeamMembers, useTeamPlan, createCheckoutSession, createPortalSession,
} from '../tareas/api';

const PRICE = { year: 5, month: 7 } as const;

export default function PlanCard() {
  const { profile } = useAuth();
  const { members } = useTeamMembers();
  const { plan, loading } = useTeamPlan();
  const [interval, setIntervalState] = useState<'year' | 'month'>('year');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const justPaid = new URLSearchParams(window.location.search).get('checkout') === 'success';
  const teamId = getActiveTeamId();
  const myRole = members.find((m) => m.id === profile?.id)?.role;
  const canManage = myRole === 'owner' || myRole === 'admin';
  const isTeam = plan?.plan === 'team';
  const seats = members.length;

  const go = async (fn: () => Promise<string>) => {
    if (!teamId) return;
    setBusy(true); setError('');
    try {
      window.location.href = await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Algo salió mal');
      setBusy(false);
    }
  };

  if (loading) return <section style={styles.card}><p style={styles.muted}>Cargando…</p></section>;

  return (
    <section style={styles.card}>
      <div style={styles.headRow}>
        <h2 style={styles.cardTitle}>Plan</h2>
        <span style={{ ...styles.badge, ...(isTeam ? styles.badgeTeam : styles.badgeFree) }}>
          {isTeam ? 'Team' : 'Free'}
        </span>
      </div>

      {justPaid && !isTeam && (
        <p style={styles.note}>Estamos confirmando tu pago… puede tardar unos segundos. Recarga la página si no se actualiza.</p>
      )}

      {isTeam ? (
        <>
          <p style={styles.help}>
            Tu espacio está en <strong>Team</strong>: usuarios ilimitados, varios espacios, 100 GB y archivos hasta 200 MB.
          </p>
          <div style={styles.metaRow}>
            <Meta label="Asientos" value={`${seats}`} />
            <Meta label="Facturación" value={plan?.billing_interval === 'month' ? 'Mensual' : 'Anual'} />
            <Meta label="Estado" value={statusLabel(plan?.subscription_status)} />
            {plan?.current_period_end && (
              <Meta label="Renueva" value={new Date(plan.current_period_end).toLocaleDateString('es-ES')} />
            )}
          </div>
          {canManage ? (
            <button type="button" onClick={() => go(() => createPortalSession(teamId!))} disabled={busy} style={styles.primary}>
              {busy ? 'Abriendo…' : 'Gestionar facturación'}
            </button>
          ) : (
            <p style={styles.muted}>Solo el propietario o un admin puede gestionar la facturación.</p>
          )}
        </>
      ) : (
        <>
          <p style={styles.help}>
            Estás en el plan <strong>Free</strong>: hasta 5 usuarios, 1 espacio, 2 GB y archivos hasta 25 MB.
            Mejora a <strong>Team</strong> para quitar los límites.
          </p>

          <div style={styles.toggle}>
            <button type="button" onClick={() => setIntervalState('year')} style={{ ...styles.toggleBtn, ...(interval === 'year' ? styles.toggleOn : null) }}>
              Anual <span style={styles.save}>−29%</span>
            </button>
            <button type="button" onClick={() => setIntervalState('month')} style={{ ...styles.toggleBtn, ...(interval === 'month' ? styles.toggleOn : null) }}>
              Mensual
            </button>
          </div>

          <div style={styles.priceRow}>
            <span style={styles.price}>{PRICE[interval]} €</span>
            <span style={styles.priceUnit}>/ usuario · mes{interval === 'year' ? ' (facturado anual)' : ''}</span>
          </div>
          <p style={styles.estimate}>
            Tu equipo tiene <strong>{seats}</strong> {seats === 1 ? 'miembro' : 'miembros'}: ~{PRICE[interval] * seats} €/mes + IVA.
          </p>

          {canManage ? (
            <button type="button" onClick={() => go(() => createCheckoutSession(teamId!, interval))} disabled={busy} style={styles.primary}>
              {busy ? 'Redirigiendo…' : 'Mejorar a Team'}
            </button>
          ) : (
            <p style={styles.muted}>Solo el propietario o un admin puede mejorar el plan.</p>
          )}
        </>
      )}

      {error && <p style={styles.error}>{error}</p>}
    </section>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.meta}>
      <div style={styles.metaLabel}>{label}</div>
      <div style={styles.metaValue}>{value}</div>
    </div>
  );
}

function statusLabel(s: string | null | undefined): string {
  switch (s) {
    case 'active': return 'Activa';
    case 'trialing': return 'En prueba';
    case 'past_due': return 'Pago pendiente';
    case 'canceled': return 'Cancelada';
    default: return s ?? '—';
  }
}

const styles: Record<string, CSSProperties> = {
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 22, marginBottom: 18 },
  headRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 },
  badge: { fontSize: 11.5, fontWeight: 800, padding: '3px 10px', borderRadius: 999, letterSpacing: 0.3 },
  badgeFree: { background: '#f1f5f9', color: '#64748b' },
  badgeTeam: { background: '#dcfce7', color: '#15803d' },
  help: { fontSize: 13.5, color: '#64748b', margin: '0 0 16px', lineHeight: 1.55 },
  note: { fontSize: 13, color: '#1d4ed8', background: '#eff5ff', border: '1px solid #dbe7fe', borderRadius: 8, padding: '10px 12px', margin: '0 0 14px' },
  metaRow: { display: 'flex', flexWrap: 'wrap', gap: 22, marginBottom: 18 },
  meta: {},
  metaLabel: { fontSize: 11.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 },
  metaValue: { fontSize: 15, fontWeight: 600, color: '#0f172a', marginTop: 2 },
  toggle: { display: 'inline-flex', gap: 4, padding: 4, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 999, marginBottom: 14 },
  toggleBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', color: '#64748b', fontSize: 13.5, fontWeight: 600, padding: '7px 14px', borderRadius: 999, cursor: 'pointer' },
  toggleOn: { background: '#2563eb', color: '#fff' },
  save: { fontSize: 10.5, fontWeight: 800, background: '#dcfce7', color: '#15803d', padding: '1px 6px', borderRadius: 999 },
  priceRow: { display: 'flex', alignItems: 'baseline', gap: 8 },
  price: { fontSize: 32, fontWeight: 800, color: '#0f172a', letterSpacing: '-1px' },
  priceUnit: { fontSize: 13.5, color: '#64748b', fontWeight: 600 },
  estimate: { fontSize: 13, color: '#64748b', margin: '6px 0 18px' },
  primary: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  muted: { color: '#94a3b8', fontSize: 13.5, margin: 0 },
  error: { color: '#dc2626', fontSize: 13, marginTop: 12, marginBottom: 0 },
};
